/**
 * SuggestedAnswers Component
 *
 * Displays pre-suggested answers when the bot asks a question (👉 emoji present).
 * These answers are generated by the backend based on:
 * - The bot's question to the visitor
 * - Conversation history
 * - Visitor profile context
 * - Common response patterns
 *
 * Features a "surfacing suggestions" animation where answers elegantly
 * slide up and fade in with staggered timing.
 */

import type React from 'react';
import { useEffect } from 'react';
import { Analytics } from '../analytics';
import { getSuggestedAnswersText } from '../config/constants';
import { useChatLanguage } from '../context/ChatThemeContext';

interface SuggestedAnswersProps {
    /** List of suggested answers */
    answers: string[];
    /** Callback when user clicks an answer */
    onAnswerClick: (answer: string) => void;
    /** Optional custom class name for styling */
    className?: string;
}

export const SuggestedAnswers: React.FC<SuggestedAnswersProps> = ({
    answers,
    onAnswerClick,
    className = '',
}) => {
    // Get the effective language using the theme context
    const effectiveLanguage = useChatLanguage();

    // Get localized label text
    const labelText = getSuggestedAnswersText('LABEL', effectiveLanguage);

    const displayedAnswers = answers;

    // Track when suggested answers list is displayed (once per list)
    // Hook must be called unconditionally, so we do checks inside
    useEffect(() => {
        // Skip tracking if no answers
        if (displayedAnswers.length === 0) {
            return;
        }

        // Create a unique key for this list of answers to prevent duplicate tracking
        const answersKey = displayedAnswers.join('|');
        const storageKey = `suggested_answers_displayed_${answersKey}`;

        // Check if we've already tracked this exact list
        if (sessionStorage.getItem(storageKey)) {
            return;
        }

        // Mark as tracked
        sessionStorage.setItem(storageKey, 'true');

        // Track the display event with all answers
        Analytics.trackSuggestedAnswersDisplayed({
            answerCount: displayedAnswers.length,
            answers: displayedAnswers,
            language: effectiveLanguage,
        });
    }, [displayedAnswers, effectiveLanguage]);

    // Handle answer click with tracking
    const handleAnswerClick = (answer: string, index: number) => {
        // Create a unique key for this specific answer click
        const storageKey = `suggested_answer_clicked_${answer}`;

        // Check if we've already tracked this answer click
        if (!sessionStorage.getItem(storageKey)) {
            // Mark as tracked
            sessionStorage.setItem(storageKey, 'true');

            // Track the click event
            Analytics.trackSuggestedAnswerClicked({
                answerText: answer,
                answerIndex: index,
                language: effectiveLanguage,
            });
        }

        // Call the original callback
        onAnswerClick(answer);
    };

    // Don't render if no answers
    if (!answers || answers.length === 0) {
        return null;
    }

    return (
        <div className={`rose-suggested-answers-container mt-6 ${className}`}>
            {/* Label fades in first */}
            <p
                className="rose-suggested-answers-label text-sm font-medium mb-3"
                style={{
                    animation: 'roseSuggestedAnswersLabelFadeIn 0.4s ease-out forwards',
                    opacity: 0,
                    color: 'var(--rw-text-primary)',
                }}
            >
                {labelText}
            </p>
            <div className="rose-suggested-answers-buttons flex flex-row flex-wrap gap-2.5">
                {displayedAnswers.map((answer, index) => (
                    <button
                        key={answer}
                        onClick={() => handleAnswerClick(answer, index)}
                        className="rose-suggested-answer-button group flex items-center justify-between py-2 text-left text-base leading-[22px] font-normal cursor-pointer transition-all duration-150 ease-in-out focus:outline-2 focus:outline-offset-2 active:translate-y-px w-fit max-w-full"
                        style={{
                            backgroundImage:
                                'linear-gradient(var(--rw-bg-chip), var(--rw-bg-chip))',
                            border: '1px solid var(--rw-border-chip)',
                            borderRadius: 'var(--rw-radius-pill)',
                            color: 'var(--rw-text-primary)',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            // Staggered entrance: each answer delayed by 100ms
                            animation: 'roseSuggestedAnswersSlideUp 0.5s ease-out forwards',
                            animationDelay: `${0.2 + index * 0.1}s`,
                            opacity: 0,
                            transform: 'translateY(12px)',
                        }}
                        type="button"
                        aria-label={`Answer: ${answer}`}
                    >
                        <span className="flex-1">{answer}</span>
                    </button>
                ))}
            </div>

            {/* Inline keyframe animations */}
            <style>{`
                @keyframes roseSuggestedAnswersLabelFadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(4px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes roseSuggestedAnswersSlideUp {
                    from {
                        opacity: 0;
                        transform: translateY(12px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .rose-suggested-answer-button {
                    background-color: var(--rw-surface-panel);
                }

                .rose-suggested-answer-button:hover {
                    background-color: var(--rw-surface-interactive-hover);
                }

                .rose-suggested-answer-button:active {
                    background-color: var(--rw-surface-interactive-active);
                }

                /* Respect user's motion preferences */
                @media (prefers-reduced-motion: reduce) {
                    .rose-suggested-answers-label,
                    .rose-suggested-answer-button {
                        animation: none !important;
                        opacity: 1 !important;
                        transform: none !important;
                    }
                }
            `}</style>
        </div>
    );
};
